/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */
import { NodeRecord, NodeRecordTypeMap } from './records.model';
import { ElementConfig, ElementConfigTypeMap } from './element-config.model';

export interface ReportsResponse {
  readonly reports: ReportMetadata[];
}

export interface ReportMetadata {
  readonly id: number;
  readonly createdAt: number;
  readonly conference: number;
  // Still to come:
  // readonly language: string;
  // readonly version: number;
  // readonly template: number;
}

export interface ReportLayout {
  readonly label: string;
  readonly icon: string;
  readonly categories: readonly Category[];
}

export interface Category {
  readonly label: string;
  readonly icon: string;
  readonly dashboards: readonly Dashboard[];
}

export interface Dashboard {
  readonly label: string;
  readonly type: DashboardType;
}

export interface Node {
  readonly id: number;
  readonly type: NodeType;
  readonly effectiveTimestamp: number | null;
  readonly owner: number;
  readonly records: readonly NodeRecord[];
}

const DASHBOARD_TYPES = ['initialSurvey', 'timeline', 'treatments', 'handover'] as const;

export type DashboardType = (typeof DASHBOARD_TYPES)[number];

export enum NodeType {
  INITIAL_SURVEY = 'initialSurvey',
  GENERIC = 'generic',
  HANDOVER = 'handover',
  SUPPLEMENTS = 'supplements',
  METADATA = 'metadata',
}
export type NodeType_ = 'initialSurvey' | 'generic' | 'handover' | 'supplements' | 'metadata';
export const UNIQUE_NODE_TYPES_PER_TACTICAL_UNIT: readonly NodeType_[] = ['initialSurvey', 'handover'] as const;

export type NodeWithDashboardType = Extract<DashboardType, NodeType_>;
export const NODE_TYPES_WITH_DASHBOARD: readonly NodeWithDashboardType[] = ['initialSurvey', 'handover'] as const;

export interface ElementsLayout {
  readonly elements: readonly ElementConfig[];
}

export type ElementType = keyof NodeRecordTypeMap & keyof ElementConfigTypeMap;

export type ElementTypeOrKey<T extends ElementType> = T | { type: T; elementKey?: T | string };

/* helper functions */

export function isKnownDashboardType(dashboard: Dashboard) {
  return (DASHBOARD_TYPES as readonly string[]).includes(dashboard.type);
}

export function isUniqueNodeTypePerTacticalUnit(nodeType: NodeType) {
  return UNIQUE_NODE_TYPES_PER_TACTICAL_UNIT.includes(nodeType);
}

export function isNodeTypeWithDashboard(nodeType: NodeType | DashboardType) {
  return (NODE_TYPES_WITH_DASHBOARD as string[]).includes(nodeType);
}

/* Returns the "elementKey" or as fallback "type" property of an object (node record or element configuration)
or the ElementType directly if it is given as a string */
export function getElementKeyOrType<T extends ElementType, O extends ElementTypeOrKey<T>>(
  elementTypeOrKey: O
): T | string {
  return typeof elementTypeOrKey === 'object'
    ? (elementTypeOrKey.elementKey ?? elementTypeOrKey.type)
    : (elementTypeOrKey as T);
}
